import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';
import './utils/adminUtils';
import AdminRoute from './components/AdminRoute';
import AdminStatsPage from './pages/AdminStatsPage';
import AdminCleanupPage from './pages/AdminCleanupPage';
import ScrollToTop from './components/ScrollToTop';
import { FloatingCoffeeButton } from './components/BuyMeACoffee';

// Fallback component for chunk loading errors
const ChunkErrorFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
    <div className="text-center max-w-md mx-auto">
      <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-red-200/50">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">⚠️</span>
        </div>
        
        <h2 className="text-xl font-bold text-red-900 mb-4">
          Eroare de încărcare
        </h2>
        
        <p className="text-red-700 mb-6 text-sm leading-relaxed">
          Nu s-a putut încărca această pagină. Te rog să încerci din nou.
        </p>
        
        <div className="space-y-3">
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Reîncarcă Pagina
          </button>
          
          <button
            onClick={() => window.location.href = '/calculator'}
            className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-2xl font-medium hover:bg-gray-200 transition-all duration-300"
          >
            Înapoi la Calculator
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Enhanced lazy loading with retry logic
const createLazyComponent = (importFunc, fallbackComponent = null) => {
  return lazy(() => 
    importFunc().catch(error => {
      console.error('Chunk loading failed:', error);
      // Retry loading once
      return new Promise((resolve) => {
        setTimeout(() => {
          importFunc().then(resolve).catch(() => {
            // If retry also fails, return fallback or reload page
            if (fallbackComponent) {
              resolve({ default: fallbackComponent });
            } else {
              window.location.reload();
            }
          });
        }, 1000);
      });
    })
  );
};

// Create lazy components with retry logic
const LoginPage = createLazyComponent(() => import('./pages/LoginPage'), ChunkErrorFallback);
const CalculatorPage = createLazyComponent(() => import('./pages/CalculatorPage'), ChunkErrorFallback);
const StatsPage = createLazyComponent(() => import('./pages/StatsPage'), ChunkErrorFallback);
const ContactPage = createLazyComponent(() => import('./pages/ContactPage'), ChunkErrorFallback);
const PrivacyPolicyPage = createLazyComponent(() => import('./pages/PrivacyPolicyPage'), ChunkErrorFallback);
const TermsOfServicePage = createLazyComponent(() => import('./pages/TermsOfServicePage'), ChunkErrorFallback);
const AboutPage = createLazyComponent(() => import('./pages/AboutPage'), ChunkErrorFallback);

// Enhanced loading component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
    <div className="text-center space-y-4">
      <div className="w-16 h-16 bg-white/80 backdrop-blur-xl border border-blue-200/50 rounded-3xl shadow-2xl flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
      <p className="text-blue-600 font-semibold">Se încarcă...</p>
    </div>
  </div>
);

// Optimized Protected Route
const ProtectedRoute = React.memo(({ children }) => {
  const { user, authInitialized } = useAuth();
  
  if (!authInitialized) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" replace />;
  
  return children;
});

// Optimized Public Route
const PublicRoute = React.memo(({ children }) => {
  const { user, authInitialized } = useAuth();
  
  if (!authInitialized) return <LoadingSpinner />;
  if (user) return <Navigate to="/calculator" replace />;
  
  return children;
});

// Memoized routes to prevent re-creation
const AppRoutes = React.memo(() => (
  <Suspense fallback={<LoadingSpinner />}>
    <Routes>
      <Route path="/" element={<PublicRoute><LoginPage /></PublicRoute>} />
      {/* Calculator is open to guests (logged-out users) so the app has public
          content; guests get persistent nudges to create an account. */}
      <Route path="/calculator" element={<CalculatorPage />} />
      <Route 
        path="/stats" 
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <StatsPage />
          </Suspense>
        } 
      />
      <Route path="/contact" element={<ProtectedRoute><ContactPage /></ProtectedRoute>} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route 
        path="/admin/stats" 
        element={
          <AdminRoute>
            <AdminStatsPage />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/cleanup" 
        element={
          <AdminRoute>
            <AdminCleanupPage />
          </AdminRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Suspense>
));

function App() {
  useEffect(() => {
    // Register service worker for caching
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      navigator.serviceWorker.register('/sw.js')
        .catch(() => {}); // Silent fail
    }

    // Prevent zoom on mobile
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.content = 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no';
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <AppRoutes />
          <FloatingCoffeeButton />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
